import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Music, Wine, Utensils, Wifi, Star, ArrowLeft, Instagram } from "lucide-react";

import Brand from "../components/Brand";
import { Button } from "../components/ui/button";

// Real plate photos of LAMUSIK pulled from the restaurant's public TheFork listing.
const GALLERY = [
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/deba8a05-3f98-4ce4-8388-3c89551cc2e0.jpg",
    alt: "Entrecôte com batata rústica, espargos e alecrim a arder",
  },
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/5f5432ee-b40c-4837-8e4b-e0dccbd2c6af.jpg",
    alt: "Camarão à conceição com molho de alho e malagueta",
  },
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/20e581be-4571-446c-be63-7979010e5e5f.jpg",
    alt: "Ovos rotos com batata frita, cebola confit e pimentos",
  },
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/ad78ac73-1a24-49c8-9a74-6fac1c4d2f7e.jpg",
    alt: "Croquetes de alheira com maionese de endro",
  },
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/76f00768-cfba-41f5-b8de-4b7679fc9275.jpg",
    alt: "Dumplings dourados com repolho e molho escuro",
  },
  {
    src: "https://cdn.thefork.com/tf-lab/image/upload/w_800,c_fill,q_auto,f_auto/restaurant/5f8e6912-c3f0-4be2-9281-5bdcf868d7b1/93620a10-609d-4449-9718-dfe36ea84f92.jpg",
    alt: "Tábua de queijo com flor comestível, maçã verde e nozes",
  },
];

// Reviews from Google (4.6★, 29 reviews — public profile).
const REVIEWS = [
  {
    text: "Fui jantar com amigos, comida ótima, música ao vivo com ambiente espectacular.",
    author: "F. V.",
    source: "Google",
  },
  {
    text: "Excelente comida, tudo cheio de sabor, chef e staff muito simpáticos.",
    author: "Fred Botta",
    source: "Google",
  },
  {
    text: "Um lugar para voltar vezes sem conta!",
    author: "Alexandra Monteiro",
    source: "Google",
  },
];

const FEATURES = [
  { icon: Music, label: "Música ao vivo · Jazz, bossa nova e soul" },
  { icon: Utensils, label: "Cozinha portuguesa de assinatura" },
  { icon: Wine, label: "Cocktails de autor" },
  { icon: Wifi, label: "Wi-Fi · Opções vegetarianas" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-black text-[#F4EFEB] grain">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/5">
        <Brand size="sm" />
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A19D98] hover:text-[#D45A2C] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </Link>
      </header>

      <main className="px-6 md:px-12 py-12 md:py-20 max-w-6xl mx-auto">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="accent-rule" />
          <p className="mt-5 text-xs uppercase tracking-[0.32em] text-[#A19D98]">
            Sobre o restaurante · Parede, Cascais
          </p>
          <h1 className="font-poster text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5">
            Onde a <span className="text-[#D45A2C]">música</span> encontra a cozinha portuguesa.
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#A19D98] leading-relaxed">
            O LAMUSIK é um espaço de jantar intimista com apenas 35 lugares, na Parede, dedicado à fusão entre
            a cozinha portuguesa de assinatura e a música ao vivo. A programação musical, com curadoria de
            Rodrigo Balona, passa pelo jazz, pela bossa nova e pelo soul — e a carta de cocktails presta
            homenagem a grandes nomes da música contemporânea, como Anderson .Paak ou Frank Ocean.
          </p>
        </motion.section>

        {/* Features */}
        <section className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="features">
          {FEATURES.map((f) => (
            <div
              key={f.label}
              className="border border-white/5 bg-[#0A0A0A] p-5 flex items-start gap-3"
            >
              <f.icon className="h-5 w-5 text-[#D45A2C] shrink-0 mt-0.5" />
              <span className="text-sm text-[#F4EFEB] leading-snug">{f.label}</span>
            </div>
          ))}
        </section>

        {/* Gallery */}
        <section className="mt-20">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-4xl">A nossa cozinha</h2>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6b6763] hidden sm:block">
              Pratos de assinatura
            </span>
          </div>
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4"
            data-testid="gallery"
          >
            {GALLERY.map((img, i) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="aspect-square overflow-hidden bg-[#0A0A0A] border border-white/5"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="mt-20" data-testid="reviews-section">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <h2 className="font-display text-3xl md:text-4xl">O que dizem de nós</h2>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-0.5 text-[#FF9500]">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="text-[#F4EFEB] font-display text-xl">4,6</span>
              <span className="text-xs uppercase tracking-[0.22em] text-[#6b6763]">· Google · 29 críticas</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {REVIEWS.map((r, i) => (
              <motion.figure
                key={r.author}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/5 bg-[#0A0A0A] p-6 flex flex-col gap-4"
                data-testid={`review-${i}`}
              >
                <div className="flex items-center gap-0.5 text-[#FF9500]">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <blockquote className="font-display text-xl text-[#F4EFEB] leading-snug">
                  &ldquo;{r.text}&rdquo;
                </blockquote>
                <figcaption className="mt-auto flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-sm text-[#A19D98]">{r.author}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#6b6763]">{r.source}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </section>

        {/* Practical info */}
        <section className="mt-20" data-testid="practical-info">
          <h2 className="font-display text-3xl md:text-4xl mb-6">Como chegar</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-white/5 bg-[#0A0A0A] p-6">
              <MapPin className="h-5 w-5 text-[#D45A2C] mb-3" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#A19D98] mb-2">Morada</p>
              <a
                href="https://maps.google.com/?q=Rua+Jos%C3%A9+Relvas+12+Parede"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F4EFEB] hover:text-[#D45A2C] transition-colors font-display text-lg leading-snug"
              >
                Rua José Relvas, 12<br />
                2775-222 Parede
              </a>
            </div>
            <div className="border border-white/5 bg-[#0A0A0A] p-6">
              <Clock className="h-5 w-5 text-[#D45A2C] mb-3" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#A19D98] mb-2">Horário</p>
              <p className="text-[#F4EFEB] text-sm leading-relaxed">
                Quarta a quinta · 19h – 24h<br />
                Sexta e sábado · 19h – 2h<br />
                <span className="text-[#6b6763]">Dom · Seg · Ter encerrado</span>
              </p>
            </div>
            <div className="border border-white/5 bg-[#0A0A0A] p-6">
              <Phone className="h-5 w-5 text-[#D45A2C] mb-3" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#A19D98] mb-2">Contacto</p>
              <a
                href="tel:+351912383745"
                className="text-[#F4EFEB] hover:text-[#D45A2C] transition-colors font-display text-lg block mb-3"
              >
                +351 912 383 745
              </a>
              <a
                href="https://www.instagram.com/lamusik.cascais/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#A19D98] hover:text-[#D45A2C] transition-colors"
              >
                <Instagram className="h-4 w-4" /> @lamusik.cascais
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20 text-center border-t border-white/5 pt-16">
          <p className="text-xs uppercase tracking-[0.32em] text-[#A19D98] mb-4">
            Vamos por uma noite na sua agenda?
          </p>
          <Link to="/">
            <Button
              data-testid="cta-reservar"
              className="bg-[#D45A2C] hover:bg-[#B84B23] text-white rounded-none px-8 py-6 text-xs uppercase tracking-[0.35em]"
            >
              Reservar mesa
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
}
